import { Box, Button, Chip, Pagination, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useState } from 'react';
import type { AdminUser, UserSort } from '../../api/admin.api';
import { useListAdminUsersQuery, useSetUserAdminMutation } from '../../api/admin.api';
import { describeQueryError } from '../../api/describeQueryError.api';
import { ContentState } from '../../components/ContentState';
import { formatDateTime } from '../../content/adminDates';
import { useAppSelector } from '../../store/hooks';
import { cardSurfaceSx } from '../../theme';

const SORT_LABELS: Record<UserSort, string> = { recent: 'Recently signed in', name: 'Name' };

/** A person is named as their name when it is known, then their email, then nothing better than that. */
const displayName = (user: AdminUser): string => user.name ?? user.email ?? 'Unnamed user';

/**
 * Everyone in the toolkit's user pool, fifty to a page, with the admins marked and
 * a button to make someone an admin or remove that. **The server sorts and pages**, so a
 * change of sort goes back to the first page.
 *
 * Nobody can remove their own admin access here: the one admin who would have to undo
 * it is the one locked out. The server refuses it too.
 */
export const UsersSection = () => {
  const [sort, setSort] = useState<UserSort>('recent');
  const [page, setPage] = useState(1);
  const { data, isLoading, error, refetch } = useListAdminUsersQuery({ sort, page });
  const [setUserAdmin, { isLoading: isSaving, error: saveError }] = useSetUserAdminMutation();
  const signedInSub = useAppSelector((state) => state.auth.user?.subject);
  const users = data?.users ?? [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center', justifyContent: 'space-between' }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          aria-label="Sort people by"
          value={sort}
          onChange={(_event, value: UserSort | null) => {
            if (value !== null) {
              setSort(value);
              setPage(1);
            }
          }}
        >
          {(Object.keys(SORT_LABELS) as UserSort[]).map((option) => (
            <ToggleButton key={option} value={option}>
              {SORT_LABELS[option]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {data !== undefined && (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {data.total === 1 ? '1 person' : `${String(data.total)} people`}
          </Typography>
        )}
      </Box>

      {saveError !== undefined && (
        <Typography role="alert" variant="body2" sx={{ color: 'error.main' }}>
          {describeQueryError(saveError)}
        </Typography>
      )}

      <ContentState
        isLoading={isLoading}
        error={error}
        onRetry={() => void refetch()}
        errorTitle="Unable to load the people"
        errorNoun="list of people"
        isEmpty={users.length === 0}
        emptyTitle="Nobody has signed in yet"
        emptyMessage="People appear here once they have signed in to the toolkit."
      >
        <Box component="ul" sx={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {users.map((user) => {
            const isSelf = user.sub === signedInSub;

            return (
              <Box
                component="li"
                key={user.sub}
                sx={{ ...cardSurfaceSx, padding: 5, display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'space-between', alignItems: 'center' }}
              >
                <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {displayName(user)}
                    </Typography>
                    {user.isAdmin && <Chip size="small" color="primary" label="Admin" />}
                    {isSelf && (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        You
                      </Typography>
                    )}
                  </Box>

                  {user.email !== undefined && user.name !== undefined && (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {user.email}
                    </Typography>
                  )}

                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {user.lastSignInAt === undefined
                      ? 'Last signed in: not recorded yet.'
                      : `Last signed in ${formatDateTime(user.lastSignInAt) ?? user.lastSignInAt}.`}
                  </Typography>

                  {isSelf && user.isAdmin && (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Another admin has to remove your access.
                    </Typography>
                  )}
                </Box>

                <Button
                  variant={user.isAdmin ? 'outlined' : 'contained'}
                  size="small"
                  disabled={isSaving || (isSelf && user.isAdmin)}
                  onClick={() => void setUserAdmin({ sub: user.sub, admin: !user.isAdmin })}
                >
                  {user.isAdmin ? 'Remove admin' : 'Make admin'}
                </Button>
              </Box>
            );
          })}
        </Box>
      </ContentState>

      {data !== undefined && data.pageCount > 1 && (
        <Pagination count={data.pageCount} page={data.page} onChange={(_event, value) => setPage(value)} />
      )}
    </Box>
  );
};
